import type { PARTICIPANT_RELATIONS_BY_ENTITY, PermissionSetEntity, RecordPermissionOf } from './permissionSetEntities'
import type { SupplierRecordPermission } from './supplierPermissions'

type ParticipantOfRelation<Relation> = Relation extends `derived_${infer Participant}` ? Participant : never

type ParticipantOf<Entity extends PermissionSetEntity> = ParticipantOfRelation<
  (typeof PARTICIPANT_RELATIONS_BY_ENTITY)[Entity][number]
>

/**
 * What a new participant permission set is seeded with, in the entity's vocabulary order. Supplier
 * contacts' `view_related_documents` is listed although `edit_documents` implies it, so it survives
 * `edit_documents` being switched off.
 */
export const DEFAULT_PARTICIPANT_PERMISSIONS = {
  supplier: {
    contact: [
      'view',
      'edit',
      'view_tprm',
      'view_related_requests',
      'view_related_agreements',
      'view_related_documents',
      'view_related_submissions',
      'edit_documents',
      'manage_internal_contacts',
      'manage_external_contacts',
      'create_product',
      'add_agreement',
    ],
  },
} as const satisfies {
  readonly [Entity in PermissionSetEntity]?: {
    readonly [Participant in ParticipantOf<Entity>]?: readonly RecordPermissionOf<Entity>[]
  }
}

type SupplierParticipantPermissionSetKey = { entity: 'supplier'; participant: 'contact' }

export interface SupplierParticipantPermissionSet extends SupplierParticipantPermissionSetKey {
  permissions: readonly SupplierRecordPermission[]
}

/** Which participant permission set: an entity and the participant whose set it is, one arm per set that exists. */
export type ParticipantPermissionSetKey = SupplierParticipantPermissionSetKey

/** A participant permission set, discriminated on `entity`; each arm holds its entity's permissions. */
export type ParticipantPermissionSet = SupplierParticipantPermissionSet
